'use client'

import { Button } from '@lastbrand/web-ui/button'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useMemo, useState } from 'react'

import { LoaderOverlayWrapperDS } from '@src/components/blocks/loader-overlay-wrapper-ds'
import { LoaderWrapperDS } from '@src/components/blocks/loader-wrapper-ds'
import {
  getInitialSelectedVariant,
  getSelectedOptionsForProductCard,
} from '@src/components/product-card/product-card.helpers'
import { CardVariant } from '@src/components/product-card/product-card.types'
import { ProductCard } from '@src/ds/blocks/product-card'
import { SectionContentContainer } from '@src/ds/blocks/section-content-container'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import { useAppInstallAccountsPresenter } from '@src/quince-site-common/src/organisms/appLayout/molecules/footer/molecules/app-install/app-install.presenter'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'

import type { WishlistItemListDSProps } from './wishlist-item-list-ds.types'

const WISHLIST_DISPLAY_NAME = 'Wishlist'
const WISHLIST_TRACKER = 'wishlist'
const TRACK_CATEGORY = 'product-list'

// Grid sizing mirrors sub-collection-section-ds: card max width driven by DS spacing token vars.
const PRODUCT_CARD_MAX_WIDTH_DESKTOP = 'calc(var(--web-ui-spacing-space-56) * 10)'

// Vertical spacing on the SectionContentContainer band (gutters + 1504px frame come from it).
const wishListBandCss = css({
  pb: 'space-80',
  position: 'relative',
})

const wishListGridCss = css.raw({
  display: 'grid',
  w: 'full',
  gridTemplateColumns: {
    base: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  justifyItems: 'center',
  columnGap: 'space-24',
  rowGap: { base: 'space-32' },
  '& > *': {
    w: 'full',
    maxW: { lg: PRODUCT_CARD_MAX_WIDTH_DESKTOP },
  },
  mt: { base: 'space-48', lg: 'space-80' },
})

// App-install banner present: the heading row absorbs the top spacing, so the grid tucks up.
// Mirrors the legacy `.wishListWrapperCompact` (2rem mobile / 3.2rem desktop).
const wishListGridCompactCss = css.raw({
  mt: { base: 'space-20', lg: 'space-32' },
})

const wishListItemWrapperCss = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
})

const emptyWishlistCss = css({
  textAlign: 'center',
  mt: { base: '70px', lg: 'space-100' },
})

const emptyWishlistTitleCss = css({
  textStyle: 'title-24-dec',
  mb: 'space-20',
})

// `isSelfWishlist` drives the DS card's desktop-only delete (trash) button — a viewer looking at their
// own wishlist can remove items via it (parity with the legacy `showDeleteBtn`). The filled-heart toggle
// remains the removal affordance on mobile and on someone else's shared wishlist.
export function WishlistItemListDS({
  items,
  isLoading = false,
  sectionConfig: { tracker },
  isSelfWishlist,
}: WishlistItemListDSProps) {
  const [selectedVariants, setSelectedVariants] = useState<Record<string, CardVariant>>({})
  const { shouldRender: shouldRenderAppInstallAccounts } = useAppInstallAccountsPresenter({ placement: 'wishlist' })

  const reversedWishlistItems = useMemo(() => {
    return items ? [...items].reverse() : []
  }, [items])

  const { t: tCommon } = useTranslation('common')
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART

  const hasData = reversedWishlistItems.length > 0

  const content = (
    <SectionContentContainer className={wishListBandCss}>
      {hasData ? (
        <div className={css(wishListGridCss, shouldRenderAppInstallAccounts ? wishListGridCompactCss : {})}>
          {reversedWishlistItems.map((productCard, index) => {
            const selectedCardVariant = getInitialSelectedVariant(productCard.product)
            const selectedColor = selectedCardVariant?.displayConfig.value
            const uniqueKey = `${productCard.product.productId}_${selectedColor}`

            return (
              <div
                key={uniqueKey}
                className={wishListItemWrapperCss}
                onClick={() => {
                  setFindingMethod[FINDING_METHOD_AGGREGATE.WISHLIST]?.()
                }}
              >
                {/*
                 * Prop parity vs the legacy branch (see wishlist-item-list-legacy.tsx):
                 * - `maxWidth` / `className` are not implemented by the DS card — sizing comes from the grid.
                 * - `showDeleteBtn` / `hideDeleteForOtherColors` drive the DS card's desktop-only trash button,
                 *   matching legacy: shown only on the viewer's own wishlist and only for the wishlisted variant.
                 */}
                <ProductCard
                  productCard={productCard}
                  tracker={tracker && `${tracker}__wishlist__${index}`}
                  hideBuyNowIcon
                  showDeleteBtn={isSelfWishlist}
                  hideDeleteForOtherColors
                  optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                  shouldShowMore
                  productClickTrackConfig={{
                    trackInternal: true,
                    trackGA: true,
                    // @i18n-ignore
                    category: TRACK_CATEGORY,
                    action: `${tracker ?? WISHLIST_TRACKER}__click_product_wishlist`,
                    value: productCard.product.relatedIds.spid.toString(),
                    context: {
                      contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_CARD,
                      contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.SUBCOLLECTION_DISPLAY,
                      // @i18n-ignore
                      contentContainerDisplayName: WISHLIST_DISPLAY_NAME,
                      findingMethod: FINDING_METHOD_AGGREGATE.WISHLIST,
                    },
                  }}
                  onVariantChange={variant => {
                    setSelectedVariants(prev => ({
                      ...prev,
                      [uniqueKey]: variant,
                    }))
                  }}
                />
                {/* Desktop-only, breakpoint-hidden per DS convention (legacy used a UA device check).
                    Fires no telemetry of its own — parity with the legacy button. */}
                <Button
                  variant="tertiary"
                  size="lg"
                  css={{
                    display: { base: 'none', lg: 'block' },
                    w: 'full',
                    mt: 'space-16',
                    // Hover adopts the primary variant's resting look (matches the legacy orange hover).
                    _hover: { bg: 'cantaloupe-100', color: 'black', boxShadow: 'none' },
                  }}
                  onClick={() => {
                    const currentVariant = selectedVariants[uniqueKey] ?? selectedCardVariant
                    productBuyNowStateManager.showBuyNowPopUp({
                      productCard: productCard,
                      selectedOptions: getSelectedOptionsForProductCard(currentVariant),
                    })
                  }}
                >
                  {(selectedVariants[uniqueKey] ?? selectedCardVariant)?.displayConfig.appExclusive
                    ? tCommon('shop_in_the_app').toUpperCase()
                    : tCommon(atcLabel)}
                </Button>
              </div>
            )
          })}
        </div>
      ) : (
        <EmptyWishListDS />
      )}
    </SectionContentContainer>
  )

  if (isLoading && !hasData) {
    return (
      <LoaderWrapperDS showLoader minHeight="100vh">
        {content}
      </LoaderWrapperDS>
    )
  }

  if (isLoading && hasData) {
    return <LoaderOverlayWrapperDS isLoading>{content}</LoaderOverlayWrapperDS>
  }

  return content
}

function EmptyWishListDS() {
  const router = useRouter()
  const { t } = useTranslation('wishlist')
  return (
    <div className={emptyWishlistCss}>
      <h2 className={emptyWishlistTitleCss}>{t('your_wishlist_is_empty')}</h2>
      <Button
        variant="secondary"
        size="lg"
        css={{ px: 'space-80', mx: 'auto' }}
        onClick={() => {
          // [STRFRNT-11724] /women works for all regions including UK — [...slug] route handles it universally.
          router.push('/women')
        }}
      >
        {t('shop_now')}
      </Button>
    </div>
  )
}
